import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCw, RotateCcw, Check } from "lucide-react";

// Returns a JPEG data-URL of the cropped area
async function getCroppedDataUrl(imageSrc, cropPixels, rotation = 0) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const radians = (rotation * Math.PI) / 180;
  // Rotate canvas around the cropped region center
  const w = cropPixels.width;
  const h = cropPixels.height;
  canvas.width = w;
  canvas.height = h;
  ctx.translate(w / 2, h / 2);
  ctx.rotate(radians);
  ctx.translate(-w / 2, -h / 2);
  ctx.drawImage(image, cropPixels.x, cropPixels.y, w, h, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.9);
}

const ImageCropperModal = ({ imageSrc, aspect = 1, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropPixels, setCropPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const onCropComplete = useCallback((_, pixels) => setCropPixels(pixels), []);

  const apply = async () => {
    if (!cropPixels) return;
    setSaving(true);
    try {
      const dataUrl = await getCroppedDataUrl(imageSrc, cropPixels, rotation);
      onConfirm(dataUrl);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-2 md:p-4" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[98vh] overflow-hidden flex flex-col" data-testid="image-cropper">
        <div className="p-3 border-b border-[#EBF4F6] flex items-center justify-between">
          <h3 className="font-extrabold text-[#333333] text-lg">قصّ وتعديل الصورة</h3>
          <button onClick={onCancel} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>
        <div className="relative flex-1 min-h-[60vh] md:min-h-[70vh] bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            showGrid={true}
            objectFit="contain"
          />
        </div>
        <div className="p-4 space-y-3 border-t border-[#EBF4F6]">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-[#333333] w-16">تكبير</span>
            <input type="range" min={1} max={5} step={0.05} value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-[#09637E]" data-testid="crop-zoom" />
            <span className="text-xs text-[#666666] font-mono w-12 text-center">{zoom.toFixed(2)}x</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-[#333333] w-16">تدوير</span>
            <button onClick={() => setRotation((r) => r - 90)} className="w-10 h-10 rounded-lg border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] flex items-center justify-center" data-testid="crop-rotate-left">
              <RotateCcw size={18} />
            </button>
            <button onClick={() => setRotation((r) => r + 90)} className="w-10 h-10 rounded-lg border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] flex items-center justify-center" data-testid="crop-rotate-right">
              <RotateCw size={18} />
            </button>
            <span className="text-sm text-[#666666]">{rotation}°</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="flex-1 h-11 rounded-lg border-2 border-[#7AB2B2] text-[#666666] font-bold hover:bg-[#EBF4F6]" data-testid="crop-cancel">إلغاء</button>
            <button onClick={apply} disabled={saving} data-testid="crop-confirm"
              className="flex-1 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-1 disabled:opacity-50">
              <Check size={18} /> {saving ? "..." : "تطبيق"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
